// utils/geometryProcessing/MeshProjection.js
import * as THREE from 'three';
import { hull } from '../hull';

/**
 * 将3D网格投影到2D平面并创建轮廓
 */
export class MeshProjection {
  /**
   * 处理单个投影
   * @param {Array} verticesArray - 3D顶点数组
   * @param {number} color - 投影的颜色
   * @param {string} plane - 投影平面 ('xy', 'yz', 或 'xz')
   * @param {function} createTextLabel - 创建文本标签的函数
   * @returns {Object|null} 包含线条和SVG内容的对象，如果处理失败则返回null
   */
  static processProjection(verticesArray, color, plane, createTextLabel) {
    if (!verticesArray || verticesArray.length === 0) return null;
    
    // 将3D点转换为2D以进行凸包计算
    const points2D = verticesArray.map((p) => {
      if (plane === "xy") return [p.x, p.y];
      if (plane === "yz") return [p.z, p.y];
      if (plane === "xz") return [p.x, p.z];
    });

    // 使用凹度为10的凸包算法计算轮廓
    const hullPoints = hull(points2D, 10);
    if (!hullPoints || hullPoints.length === 0) return null;
    
    // 创建可视化表示
    const material = new THREE.LineBasicMaterial({ color: color });
    const geometry = new THREE.BufferGeometry();
    const vertices = [];

    // 从凸包点创建顶点数组
    hullPoints.forEach((point) => {
      vertices.push(point[0], point[1], 0);
    });
    // 闭合形状
    vertices.push(hullPoints[0][0], hullPoints[0][1], 0);

    geometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(vertices, 3)
    );
    
    const line = new THREE.Line(geometry, material);
    
    // 根据投影平面应用适当的旋转
    if (plane === "xy") {
      line.rotation.x = Math.PI / 2; // 旋转以匹配XY平面
    } else if (plane === "yz") {
      line.rotation.z = Math.PI / 2; // 旋转以匹配YZ平面
    } else {
      // XZ平面不需要额外旋转
    }
    
    // 计算文本标签的中心
    const centerX = hullPoints.reduce((sum, point) => sum + point[0], 0) / hullPoints.length;
    const centerY = hullPoints.reduce((sum, point) => sum + point[1], 0) / hullPoints.length;
    
    // 定义更具描述性的投影名称
    let planeDescription = "";
    if (plane === "xy") {
      planeDescription = "Top View (XY)";
    } else if (plane === "yz") {
      planeDescription = "Side View (YZ)";
    } else { // xz
      planeDescription = "Front View (XZ)";
    }
    
    // 创建带有更多信息的文本标签
    if (createTextLabel) {
      const textLabel = createTextLabel(planeDescription, new THREE.Vector3(centerX, centerY, 0), {
        fontSize: 14,
        backgroundColor: "rgba(255, 255, 255, 0.7)",
        textColor: "#000000",
        padding: 5,
        scaleFactor: 0.5
      });
      
      line.add(textLabel);
    }
    
    // 生成用于导出的SVG
    let svgContent = '<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200">';
    const pathData = "M " + hullPoints.map((point) => `${point[0]},${point[1]}`).join(" L ") + " Z";
    const colorStr = `#${color.toString(16).padStart(6, '0')}`;
    
    svgContent += `<path d="${pathData}" fill="none" stroke="${colorStr}" stroke-width="2"/>`;
    svgContent += `<text x="${centerX}" y="${centerY}" font-family="Arial" font-size="12" fill="black" text-anchor="middle" dominant-baseline="middle">${plane.toUpperCase()}</text>`;
    svgContent += "</svg>";
    
    return { 
      line, 
      svgContent, 
      hullPoints, 
      centerX, 
      centerY, 
      planeDescription 
    };
  }

  /**
   * 从3D模型几何体生成所有平面(XY, YZ, XZ)的投影
   * @param {THREE.BufferGeometry} geometry - Three.js几何体
   * @param {function} createTextLabel - 创建文本标签的函数
   * @returns {Object} 包含所有投影和SVG数据的对象
   */
  static generateProjections(geometry) {
    if (!geometry || !geometry.attributes || !geometry.attributes.position) {
      console.error("无效的几何体：缺少position属性");
      return {
        projections: {},
        svgData: {}
      };
    }
    
    // 从几何体提取位置
    const positions = geometry.attributes.position;
    
    // 为三个平面准备投影点
    const projections = {
      xy: [],
      yz: [],
      xz: []
    };

    // 遍历所有顶点，为每个平面创建投影点
    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i);
      const y = positions.getY(i);
      const z = positions.getZ(i);

      projections.xy.push(new THREE.Vector3(x, y, 0));
      projections.yz.push(new THREE.Vector3(0, y, z));
      projections.xz.push(new THREE.Vector3(x, 0, z));
    }
    
    return projections;
  }
  
  /**
   * 处理生成的投影，获取轮廓和SVG数据
   * @param {Object} projections - 投影点对象 {xy: [], yz: [], xz: []}
   * @param {function} createTextLabel - 创建文本标签的函数（可选）
   * @returns {Object} 包含投影结果和SVG数据的对象
   */
  static processProjections(projections, createTextLabel = null) {
    // 处理投影，生成线条和SVG内容
    const projectionResults = {
      xy: this.processProjection(projections.xy, 0xff0000, "xy", createTextLabel),
      yz: this.processProjection(projections.yz, 0x00ff00, "yz", createTextLabel),
      xz: this.processProjection(projections.xz, 0x0000ff, "xz", createTextLabel)
    };
    
    // 提取SVG数据以供导出
    const svgData = {
      xy: projectionResults.xy?.svgContent || null,
      yz: projectionResults.yz?.svgContent || null,
      xz: projectionResults.xz?.svgContent || null
    };
    
    return {
      projectionResults,
      svgData
    };
  }
  
  /**
   * 从线条几何体中提取点
   * @param {THREE.BufferGeometry} lineGeometry - 线条几何体
   * @returns {Array} 点数组 [[x1, y1], [x2, y2], ...]
   */
  static extractPointsFromLine(lineGeometry) {
    if (!lineGeometry || !lineGeometry.attributes || !lineGeometry.attributes.position) {
      return [];
    }
    
    const positions = lineGeometry.attributes.position.array;
    const points = [];
    let minX = Infinity, minY = Infinity;

    for (let i = 0; i < positions.length; i += 3) {
      const x = positions[i];
      const y = positions[i + 1];
      points.push([x, y]);

      // 跟踪最小值以便后续居中处理
      if (x < minX) minX = x;
      if (y < minY) minY = y;
    }

    // 通过减去最小值来标准化点，使形状从(0,0)开始
    return points.map(([x, y]) => [x - minX, y - minY]);
  }

  /**
   * 将投影添加到场景中并配置它们的位置
   * @param {Object} projectionResults - 投影结果对象
   * @param {THREE.Scene} scene - Three.js场景
   * @param {number} spacing - 投影之间的间距
   */
  static addProjectionsToScene(projectionResults, scene, spacing = 100) {
    if (!scene) return;
    
    Object.keys(projectionResults).forEach((plane, index) => {
      const result = projectionResults[plane];
      if (result?.line) {
        // 设置投影线的位置，错开排列
        result.line.position.set((index - 1) * spacing, 0, 0);
        scene.add(result.line);
      }
    });
  }
  
  /**
   * 导出投影作为SVG文件
   * @param {Object} svgData - 包含SVG内容的对象 {xy: string, yz: string, xz: string}
   */
  static exportSVG(svgData) {
    // 下载SVG文件的函数
    const downloadSVG = (svgContent, filename) => {
      if (!svgContent) return;
      
      const blob = new Blob([svgContent], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };
    
    // 下载每个平面的投影
    if (svgData.xy) downloadSVG(svgData.xy, "foam_cutting_template_xy.svg");
    if (svgData.yz) downloadSVG(svgData.yz, "foam_cutting_template_yz.svg");
    if (svgData.xz) downloadSVG(svgData.xz, "foam_cutting_template_xz.svg");
  }
}